<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="600">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeAsset">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Install New Asset</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-row>
                        <v-col class="pb-5 d-flex justify-center" cols="12">
                            <v-avatar :image="image" rounded="sm" size="100"/>
                            <v-btn flat icon size="small" @click="$refs.imageHandle.click()">
                                <v-icon icon="mdi-pencil"></v-icon>
                            </v-btn>
                            <v-file-input ref="imageHandle" v-model="formData['image']" accept="image/*" class="d-none"
                                          @change="validateFileSize"
                            />
                        </v-col>
                    </v-row>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>

                    <v-row class="border-b mb-0">
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['asset_type_id']" v-model:menu="assetTypeMenuOpen"
                                      :items="assetTypes"
                                      :rules="[vRules.required]"
                                      density="compact" item-title="name" item-value="id" label="Asset Type"
                                      @update:modelValue="() => $nextTick(() => assetNameRef?.focus())">
                            </v-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field ref="assetNameRef" v-model="formData['label']" :rules="[vRules.required,vRules.maxLength(64),vRules.regex(/^[a-zA-Z0-9 \-]+$/, 'Only letters, numbers, spaces and dashes allowed')]"
                                          density="compact" label="Asset Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['customer_reference']" density="compact"
                                          label="Customer Reference (optional)" maxlength="100" clearable
                                          hint="Your internal ID or reference for this asset"/>
                        </v-col>
                    </v-row>

                    <v-row v-if="['vehicle','equipment'].includes(selectedAssetType?.category)" class="border-b mb-0">
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['vin']" density="compact" label="VIN Number"
                                          maxlength="17" counter clearable
                                          hint="17-character Vehicle Identification Number"
                                          @update:modelValue="onVinInput"/>
                            <div v-if="vinLoading" class="d-flex align-center mt-n1 mb-2">
                                <v-progress-circular size="14" width="2" indeterminate color="primary" class="mr-2"/>
                                <span class="text-caption text-medium-emphasis">Decoding VIN...</span>
                            </div>
                            <v-card v-else-if="vinDetails && vinHasData" variant="tonal" color="success" density="compact" class="mt-n1 mb-2">
                                <v-card-text class="pa-3 d-flex align-start">
                                    <v-icon icon="mdi-car-info" size="20" class="mr-2 mt-1" color="success"/>
                                    <div class="flex-grow-1">
                                        <div class="text-caption font-weight-bold text-success mb-1">Vehicle Identified</div>
                                        <div v-if="vinDetails.manufacturer && vinDetails.manufacturer !== 'Unknown'" class="text-caption"><strong>Make:</strong> {{ vinDetails.manufacturer }}</div>
                                        <div v-if="vinDetails.vehicle_type && vinDetails.vehicle_type !== 'Unknown'" class="text-caption"><strong>Body:</strong> {{ vinDetails.vehicle_type }}</div>
                                        <div v-if="vinDetails.model_year && vinDetails.model_year !== 'Unknown'" class="text-caption"><strong>Year:</strong> {{ vinDetails.model_year }}</div>
                                        <div v-if="vinDetails.country && vinDetails.country !== 'Unknown'" class="text-caption"><strong>Country:</strong> {{ vinDetails.country }}</div>
                                    </div>
                                    <v-tooltip location="top" :text="vinDetails.check_digit_valid ? 'Valid VIN' : 'Invalid check digit'">
                                        <template #activator="{ props: tp }">
                                            <v-icon v-bind="tp" :icon="vinDetails.check_digit_valid ? 'mdi-check-circle' : 'mdi-alert-circle'" size="18" :color="vinDetails.check_digit_valid ? 'success' : 'warning'" class="ml-2 mt-1"/>
                                        </template>
                                    </v-tooltip>
                                </v-card-text>
                            </v-card>
                            <div v-else-if="vinDetails && !vinHasData" class="text-caption text-medium-emphasis mt-n1 mb-2">
                                <v-icon icon="mdi-information-outline" size="14" class="mr-1"/>VIN not recognized in our database
                            </div>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['odometer']"
                                          density="compact" label="Initial Odometer"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['license_plate']" density="compact"
                                          label="License Plate" maxlength="15" clearable/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['owner_email']" density="compact"
                                          label="Owner Email (optional)" type="email" clearable
                                          hint="Used for CARB compliance notifications"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['driver_id']"
                                      :items="driversList"
                                      :item-title="d => `${d.user_first_name || d.first_name || ''} ${d.user_last_name || d.last_name || ''}`.trim()"
                                      clearable density="compact"
                                      item-value="id" :label="`Assign ${personLabel}`"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['vehicle_status_id']"
                                      :items="vehicleStatuses"
                                      density="compact" item-title="name" item-value="id" label="Vehicle Status"/>
                        </v-col>
                    </v-row>

                    <v-row>

                        <v-col v-if="user.type === 'admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']"
                                      :items="companies"
                                      :rules="[vRules.required]"
                                      density="compact" label="Company"
                                      @update:modelValue="()=>{fetchDevices(); fetchAssetGroups(); fetchDriversList();}">
                            </company-select>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['asset_group_ids']"
                                      :items="assetGroups"
                                      density="compact" item-title="name" item-value="id" label="Asset Group (Optional)" multiple>
                            </v-select>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['device_id']" :items="devices"
                                      clearable density="compact" item-title="slno" item-value="id" label="Tracking Device">
                            </v-select>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, onBeforeUnmount, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useLoadingStore} from "@/stores/loadingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useOnboardingStore} from "@/stores/onboardingStore";
import {getCategoryConfig} from "@/composables/categoryConfig";
import placeholder from "@/assets/images/placeholder.png";
import CompanySelect from "@/components/globals/company-select.vue";

const assetTypeMenuOpen = ref(false);
const assetNameRef = ref(null);
const image = ref(placeholder);
const {user} = useAuthStore();
const devices = ref([]);
const companies = ref([]);
const assetTypes = ref([]);
const assetGroups = ref([]);
const driversList = ref([]);
const vehicleStatuses = ref([]);
const formReady = ref(false);
const loadingStore = useLoadingStore();
const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    preSelectedDevice: {type: Object, default: null},
    duplicateFrom: {type: Object, default: null},
});
const formData = reactive({
    valid: false,
    label: null,
    customer_reference: null,
    vin: null,
    license_plate: null,
    owner_email: null,
    image: null,
    odometer: null,
    driver_id: null,
    device_id: null,
    status: 'enabled',
    asset_type_id: null,
    asset_group_ids: null,
    vehicle_status_id: null,
    company_id: ((user.type !== 'admin') ? user.company.id : null),
});
const emits = defineEmits(['update:modelValue', 'addAsset']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);
const vinDetails = ref(null);
const vinLoading = ref(false);
let vinDebounceTimer = null;

onMounted(() => {
    const promises = [fetchAssetTypes(), fetchVehicleStatuses()];
    if (user.type === 'admin') {
        promises.push(fetchCompanies());
    } else {
        promises.push(fetchDevices(), fetchAssetGroups(), fetchDriversList());
    }
    Promise.all(promises).finally(() => {
        // Pre-select device if provided
        if (props.preSelectedDevice) {
            const exists = devices.value.some(d => d.id === props.preSelectedDevice.id);
            if (!exists) {
                devices.value.unshift(props.preSelectedDevice);
            }
            formData['device_id'] = props.preSelectedDevice.id;
        }
        if (props.duplicateFrom) {
            const s = props.duplicateFrom;
            if (s.asset_type_id != null) formData['asset_type_id'] = s.asset_type_id;
            if (s.vin) formData['vin'] = s.vin;
            if (s.odometer) formData['odometer'] = s.odometer;
            if (s.vehicle_status_id) formData['vehicle_status_id'] = s.vehicle_status_id;
            if (s.asset_groups) formData['asset_group_ids'] = s.asset_groups.map(g => g.id);
            if (s.company_id && user.type === 'admin') formData['company_id'] = s.company_id;
            // label and device_id left empty - user must provide unique values
        }
        formReady.value = true;
        if (!props.duplicateFrom) setTimeout(() => assetTypeMenuOpen.value = true, 300);
        if (formData['vin'] && formData['vin'].trim().length === 17) {
            onVinInput(formData['vin']);
        }
    });
});

const selectedAssetType = computed(() => {
    if (formData['asset_type_id']) {
        return assetTypes.value.find((assetType) => assetType.id === formData['asset_type_id']);
    }
    return null;
})
const personLabel = computed(() => getCategoryConfig(selectedAssetType.value?.category).personLabel || 'Driver');
const vinHasData = computed(() => {
    if (!vinDetails.value) return false;
    const d = vinDetails.value;
    return (d.manufacturer && d.manufacturer !== 'Unknown') ||
           (d.vehicle_type && d.vehicle_type !== 'Unknown') ||
           (d.model_year && d.model_year !== 'Unknown') ||
           (d.country && d.country !== 'Unknown');
});
const imagePreview = () => {
    image.value = URL.createObjectURL(formData['image']);
}
const validateFileSize = () => {
    const maxSize = 1024 * 1024; // 1MB in bytes
    if (formData['image'].size > maxSize) {
        showSnackbar("File size exceeds 1MB. Please upload a smaller file.", 'error');
        return false;
    }
    imagePreview();
}
const fetchCompanies = () => {
    return send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const fetchDevices = () => {
    return send('get', `devices-list`, {asset: true, company: formData['company_id']})
        .then((response) => {
            devices.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Devices could not be loaded', 'error')
        })
}
const fetchAssetTypes = () => {
    return send('get', `asset-types`, {list: true})
        .then((response) => {
            assetTypes.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset Types could not be loaded', 'error')
        })
}
const fetchVehicleStatuses = () => {
    return send('get', `fleet/statuses`, {list: 1})
        .then((response) => {
            vehicleStatuses.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Statuses could not be loaded', 'error')
        })
}
const fetchAssetGroups = () => {
    formData['asset_group_ids'] = null;
    return send('get', `asset-groups?list=true&company=${formData['company_id']}`, null)
        .then((response) => {
            assetGroups.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset Groups could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    return send('get', `companies/drivers?list=true`, {company: formData['company_id']})
        .then((response) => {
            driversList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
let currentVinLookup = null;
const onVinInput = (val) => {
    vinDetails.value = null;
    clearTimeout(vinDebounceTimer);
    const vin = (val || '').trim();
    if (vin.length !== 17) { vinLoading.value = false; return; }
    vinLoading.value = true;
    currentVinLookup = vin;
    vinDebounceTimer = setTimeout(() => {
        send('get', `vin/lookup`, {vin})
            .then((response) => {
                if (currentVinLookup !== vin) return;
                vinDetails.value = response.data?.data || null;
            })
            .catch(() => {})
            .finally(() => {
                if (currentVinLookup === vin) vinLoading.value = false;
            });
    }, 500);
};
const storeAsset = () => {
    errors.value = [];
    loadingStore.showLoading('Creating...', 'Please wait while we save your changes');
    send('post', `assets`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                useOnboardingStore().markAssetCreated();
                emits('addAsset', response.data.data.asset);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => loadingStore.hideLoading('Creating...'));
}

onBeforeUnmount(() => {
    clearTimeout(vinDebounceTimer);
});

</script>
